/**
 * Project Analytics — one route, two engines.
 *
 * When the site's analytics are provisioned as a YOM managed org
 * (`GET /projects/:id/analytics/status` → provisioned + strategy
 * 'managed-org'), the page renders YOM reports natively. Anything else
 * (flag off, endpoint not deployed yet, legacy plugin setup) keeps the
 * existing page working unchanged. With managed orgs on but this site not yet
 * provisioned (backfill pending), it says so instead of asking for a plugin.
 */
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { analyticsEmbedHelpers } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { isSettingUp, usesYomEmbeds } from '@/lib/yom/types';
import { SettingUpState } from '@/components/analytics/yom/FirstRunState';
import { ProjectYomAnalytics } from '@/components/analytics/yom/ProjectYomAnalytics';
import { DashboardSkeleton } from '@/components/analytics/yom/ReportSkeleton';
import LegacyAnalytics from '@/pages/dashboard/Analytics';

export default function AnalyticsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const statusQ = useQuery({
    queryKey: qk.analyticsStatus(projectId),
    queryFn: () => analyticsEmbedHelpers.getStatus(projectId!),
    enabled: Boolean(projectId),
    staleTime: 60_000,
    // A 404 here means the backend predates the endpoint: fall back, don't retry.
    retry: false,
  });

  if (!projectId) return null;

  if (statusQ.isPending) {
    return (
      <div className="space-y-6" aria-busy>
        <div className="space-y-2">
          <div className="bg-muted h-7 w-32 animate-pulse rounded" />
          <div className="bg-muted h-4 w-48 animate-pulse rounded" />
        </div>
        <DashboardSkeleton />
      </div>
    );
  }

  if (statusQ.data && usesYomEmbeds(statusQ.data)) {
    return <ProjectYomAnalytics projectId={projectId} status={statusQ.data} />;
  }
  if (statusQ.data && isSettingUp(statusQ.data)) {
    return (
      <SettingUpState
        projectId={projectId}
        onCheckAgain={() => void statusQ.refetch()}
        checking={statusQ.isFetching}
      />
    );
  }
  return <LegacyAnalytics />;
}
